import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Card, Pagination } from 'react-bootstrap';
import { Link, useParams } from 'react-router-dom';
import { 
  FaUtensils, FaMusic, FaCar, FaCamera, FaPaintBrush, FaCut, FaStar,
  FaShoePrints, FaCalendarAlt, FaMicrophone, FaHotel, FaUsers, FaTshirt,
  FaWineGlassAlt, FaGlassCheers, FaGift, FaBirthdayCake, FaSuitcase,
  FaHeart, FaRing, FaGem, FaChair, FaSearch, FaMapMarkerAlt, FaArrowLeft
} from 'react-icons/fa';
import { spApi } from '../../api/serviceProviders/spApi';
import styles from '../styles/WatoaHuduma.module.css';
import SpinnerLoader from '../../components/Buttons/SpinnerLoader';

// Map category names to icons
const getCategoryIcon = (categoryName) => {
  const iconMap = {
    'usafiri': <FaCar />,
    'vinywaji': <FaWineGlassAlt />,
    'magauri_ya_harusi': <FaGlassCheers />,
    'pochi': <FaGift />,
    'keki': <FaBirthdayCake />,
    'zawadi_gift_shop': <FaGift />,
    'tarumbeta': <FaMusic />,
    'saluni': <FaCut />,
    'suti': <FaSuitcase />,
    'viatu': <FaShoePrints />,
    'ndafu': <FaStar />,
    'stationery': <FaPaintBrush />,
    'planner': <FaCalendarAlt />,
    'mshereheshaji': <FaMicrophone />,
    'honeymoon': <FaHeart />,
    'chakula': <FaUtensils />,
    'pete': <FaRing />,
    'hoteli': <FaHotel />,
    'vito_jewelry': <FaGem />,
    'nguo': <FaTshirt />,
    'picha': <FaCamera />,
    'ukumbi': <FaChair />,
    'maids': <FaUsers />,
    'mapambo': <FaPaintBrush />,
    'make-up': <FaPaintBrush />,
    'muziki': <FaMusic />
  };
  
  const key = categoryName?.toLowerCase().replace(/\s+/g, '_') || '';
  
  if (iconMap[key]) return iconMap[key];
  
  for (const [mapKey, icon] of Object.entries(iconMap)) {
    if (key.includes(mapKey)) return icon;
  }
  
  return <FaStar />;
};

const WatoaHudumaByCategory = () => {
  const { id } = useParams();
  const [category, setCategory] = useState(null);
  const [serviceProviders, setServiceProviders] = useState([]);
  const [pagination, setPagination] = useState({ current_page: 1, total_pages: 1 });
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);

  // Fetch category details + providers (with optional search)
  const fetchCategoryData = useCallback(async () => {
    try {
      if (searchTerm) {
        setSearching(true);
      } else {
        setLoading(true);
      }

      const result = await spApi.getCategoryDetails(id, searchTerm, currentPage);
      
      if (result.category) {
        setCategory(result.category);
      }
      setServiceProviders(result.providers || []);
      if (result.pagination) {
        setPagination(result.pagination);
      }
    } catch (error) {
      console.error('Error fetching category data:', error);
    } finally {
      setLoading(false);
      setSearching(false);
    }
  }, [id, searchTerm, currentPage]);

  // Initial data fetch and pagination/search changes
  useEffect(() => {
    fetchCategoryData();
  }, [fetchCategoryData]);

  // Debounced search - updates searchTerm which triggers fetch
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      if (searchInput !== searchTerm) {
        setSearchTerm(searchInput);
        setCurrentPage(1); // Reset to page 1 on new search
      }
    }, 500);

    return () => clearTimeout(debounceTimer);
  }, [searchInput, searchTerm]);

  const handleSearchChange = (e) => {
    setSearchInput(e.target.value);
  };

  const handleClearSearch = () => {
    setSearchInput('');
    setSearchTerm('');
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= pagination.total_pages) {
      setCurrentPage(page);
    }
  };

  const categoryName = category?.name || 'Category';
  const categoryPhoto = category?.photo?.sm_path || category?.photo?.md_path || category?.photo?.lg_path;
  const fallbackImage = category?.photo?.md_path || category?.photo?.sm_path || category?.photo?.lg_path || 'https://via.placeholder.com/300x200';

  return (
    <Container className={styles.container}>
      <br /><br /><br />
      
      {/* Back Link */}
      <div className="mb-3">
        <Link to="/WatoaHuduma" className={styles.backLink}>
          <FaArrowLeft style={{ marginRight: '8px' }} />
          Rudi Nyuma
        </Link>
      </div>

      {/* Category Header */}
      <div className="text-center mb-4">
        {categoryPhoto && (
          <img 
            src={categoryPhoto} 
            alt={categoryName} 
            className="mb-3" 
            style={{ 
              width: '90px', 
              height: '90px', 
              objectFit: 'cover', 
              borderRadius: '50%', 
              border: '3px solid #D4AF37',
              boxShadow: '0 4px 15px rgba(212, 175, 55, 0.3)'
            }} 
          />
        )}
        <h2 className="stylish-heading">
          {getCategoryIcon(categoryName)} {categoryName}
        </h2>
        {category?.description && (
          <p className="text-muted" style={{ maxWidth: '600px', margin: '0 auto' }}>
            {category.description}
          </p>
        )}
        <p className="text-muted mt-2">
          Watoa huduma katika kategoria hii
          {!loading && !searching && pagination.total_records !== undefined && (
            <span style={{ fontWeight: 600, color: '#4169e1' }}>
              {' '} — {pagination.total_records} wamepatikana
            </span>
          )}
        </p>
      </div>

      {/* Search Section */}
      <Row className="justify-content-center mb-5">
        <Col xs={12} md={8} lg={6}>
          <div className={styles.centeredSearchContainer}>
            <div className={styles.searchWrapper}>
              <FaSearch className={styles.searchIcon} />
              <input
                type="text"
                placeholder={`Tafuta watoa huduma wa ${categoryName}...`}
                value={searchInput}
                onChange={handleSearchChange}
                className={styles.centeredSearchInput}
              />
              {searching && (
                <div style={{ 
                  position: 'absolute', 
                  right: '20px', 
                  top: '50%', 
                  transform: 'translateY(-50%)',
                  color: '#6c757d',
                  fontSize: '0.85rem',
                  zIndex: 10
                }}>
                  Inatafuta...
                </div>
              )}
            </div>
            {searchInput && (
              <p className={styles.searchHint} style={{ cursor: 'pointer', color: '#4169e1' }} onClick={handleClearSearch}>
                ✕ Ondoa utafutaji
              </p>
            )}
          </div>
        </Col>
      </Row>

      {/* Service Providers Cards */}
      {loading ? (
        <SpinnerLoader />
      ) : (
        <>
          <Row className={styles.eventsGrid}>
            {serviceProviders.length > 0 ? (
              serviceProviders.map((provider) => (
                <Col key={provider.id} xs={12} md={6} lg={4} className="mb-4">
                  <Card className={styles.eventCard}>
                    <Link to={`/watoa-huduma-details/${provider.id}`} state={{ provider, category, fallbackImage }}>
                      <Card.Img 
                        variant="top" 
                        src={provider.wallpaper?.sm_photo || provider.wallpaper?.md_photo || provider.wallpaper?.lg_photo || fallbackImage} 
                        alt={provider.name} 
                        className={styles.cardImage} 
                      />
                    </Link>
                    <Card.Body className={styles.cardBody}>
                      <Card.Title className={styles.cardTitle}>{provider.name}</Card.Title>
                      
                      {/* Location */}
                      {provider.location && (
                        <div className={styles.cardLocation}>
                          <FaMapMarkerAlt className={styles.locationIcon} />
                          <span>{provider.location}</span>
                        </div>
                      )}

                      {/* Categories */}
                      <div className={styles.cardCategories}>
                        {provider.categories?.slice(0, 3).map((cat, idx) => {
                          const catName = cat.category || cat.name || '';
                          const catIcon = getCategoryIcon(catName);
                          
                          return (
                            <span key={idx} className={styles.categoryTag}>
                              <span className={styles.tagIcon}>{catIcon}</span>
                              {catName}
                            </span>
                          );
                        })}
                        {provider.categories?.length > 3 && (
                          <span className={styles.categoryTag}>
                            +{provider.categories.length - 3}
                          </span>
                        )}
                      </div>

                      <div className={styles.buttonContainer}>
                        <Link to={`/watoa-huduma-details/${provider.id}`} state={{ provider, category, fallbackImage }}>
                          <button className={styles.viewDetailsBtn}>
                            Angalia Huduma
                          </button>
                        </Link>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              ))
            ) : (
              <Col xs={12} className="text-center py-5">
                <div className={styles.noResults}>
                  <h4>Hakuna watoa huduma waliopatikana</h4>
                  <p className="text-muted">
                    {searchInput 
                      ? <>Jaribu kubadilisha vigezo vya utafutaji au <span className={styles.resetLink} onClick={handleClearSearch} style={{ cursor: 'pointer' }}>ondoa utafutaji</span></>
                      : 'Hakuna watoa huduma katika kategoria hii kwa sasa'
                    }
                  </p>
                </div>
              </Col>
            )}
          </Row>

          {/* Pagination */}
          {pagination.total_pages > 1 && (
            <Row className="justify-content-center mt-4 mb-5">
              <Col xs="auto">
                <Pagination>
                  <Pagination.First onClick={() => handlePageChange(1)} disabled={currentPage === 1} />
                  <Pagination.Prev onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} />
                  
                  {[...Array(pagination.total_pages)].map((_, i) => {
                    const pageNum = i + 1;
                    // Show some pages around current page
                    if (pageNum === 1 || pageNum === pagination.total_pages || (pageNum >= currentPage - 2 && pageNum <= currentPage + 2)) {
                      return (
                        <Pagination.Item 
                          key={pageNum} 
                          active={pageNum === currentPage}
                          onClick={() => handlePageChange(pageNum)}
                        >
                          {pageNum}
                        </Pagination.Item>
                      );
                    } else if (pageNum === currentPage - 3 || pageNum === currentPage + 3) {
                      return <Pagination.Ellipsis key={pageNum} />;
                    }
                    return null;
                  })}

                  <Pagination.Next onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === pagination.total_pages} />
                  <Pagination.Last onClick={() => handlePageChange(pagination.total_pages)} disabled={currentPage === pagination.total_pages} />
                </Pagination>
              </Col>
            </Row>
          )}
        </>
      )}
    </Container>
  );
};

export default WatoaHudumaByCategory;